import React from 'react';
import getErrors from './validate';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import buttonStyles from '../../../styles/buttons.module.css';
import { Dialog, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import Render from '../../Render';
import { Spinner } from '@developers/ui';

const initialForm = {
  name: '',
  company: '',
  cardNumber: '',
  expMonth: '',
  expYear: '',
  cvv: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  state: '',
  internationalAddress: false,
  addressBlock: ''
}

const initialErrors = {
  cardNumber: '',
  name: '',
  address1: '',
  city: '',
  state: '',
  postalCode: '',
  expMonth: '',
  expYear: '',
  cvv: '',
  addressBlock: ''
}

export default function BillingInfoForm(props) {

  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState(initialErrors);

  const currentYear = new Date().getFullYear();
  let expirationYears = [];

  for (let i = currentYear; i <= (currentYear + 10); i++) {
    expirationYears.push(i);
  }

  React.useEffect(() => {
    if (props.creditCard?.number) {
      const expirationArray = props.creditCard.expiration.split('-');

      setForm({
        name: props.creditCard.name,
        company: props.creditCard.company,
        cardNumber: '',
        expMonth: expirationArray[1],
        expYear: expirationArray[0],
        cvv: '',
        address1: props.creditCard.address_1,
        address2: props.creditCard.address_2,
        postalCode: props.creditCard.zip,
        city: props.creditCard.city,
        state: props.creditCard.state ? props.creditCard.state?.state_id : 0,
        internationalAddress: !props.purchaseCardAddressBlock ? false : true,
        addressBlock: props.purchaseCardAddressBlock
      });
    }
  }, [props.creditCard]);

  React.useEffect(() => {
    if (props.updateCreditCardMeta.success) {
      setTimeout(() => {
        props.setOpenCreditCardForm(false)
      }, 2000);
    }
  }, [props.updateCreditCardMeta.success]);

  function handleFormChange(e, field) {
    setForm({ ...form, [field]: e.target.value });
  }

  function formatCardNumber(number) {
    const regex = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})$/g
    const onlyNumbers = number.replace(/[^\d]/g, '')

    return onlyNumbers.replace(regex, (regex, $1, $2, $3, $4) =>
      [$1, $2, $3, $4].filter(group => !!group).join('-')
    );
  }

  function handleCancel(e) {
    e.preventDefault();
    props.setOpenCreditCardForm(false)
  }

  function handleSave(e) {
    e.preventDefault();
    
    const formErrors = getErrors(form);

    if (formErrors) {
      setErrors(formErrors);
    } else {
      setErrors(initialErrors);

      let formData = form;
      if (formData.internationalAddress) {
        formData.address1 = '';
        formData.address2 = '';
        formData.city = '';
        formData.state = '';
        formData.postalCode = '';
      } else {
        formData.addressBlock = '';
      }

      props.submitCreditCardChangeForm(formData);
    }
  }

  function getInputStyles(column, styles) {
    if (errors[column] !== '') {
      return { ...styles, border: '1px solid red' };
    } else {
      return styles;
    }
  }

  function getLabelStyles(column, styles) {
    if (errors[column] !== '' && errors[column]) {
      return { ...styles, color: 'red' };
    } else {
      return styles;
    }
  }

  return (
    <Dialog 
      open={props.openCreditCardForm}
      onClose={() => props.setOpenCreditCardForm(false)}
      fullWidth
      maxWidth='sm'
    >
      <Spinner show={props.updateCreditCardMeta.loading} />
      <DialogTitle>Edit Billing Information</DialogTitle>
      <DialogContent style={{ paddingBottom: 5 }}>
        <form>
          <div className='row' style={{ paddingTop: 10 }}>
            <div className='col-sm-12'>
              <label style={getLabelStyles('name', { margin: 0, fontSize: 12 })}>
                Name on Card:
              </label>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12'>
              <input
                type='text'
                placeholder='Name on Card'
                value={form.name}
                onChange={(e) => handleFormChange(e, 'name')}
                style={getInputStyles('name', { width: '97.5%', margin: 0 })}
              />
            </div>
          </div>
          <div className='row' style={{ paddingTop: 10 }}>
            <div className='col-sm-12'>
              <label style={{ margin: 0, fontSize: 12 }}>
                Company:
              </label>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12'>
              <input
                type='text'
                placeholder='Company'
                value={form.company}
                onChange={(e) => handleFormChange(e, 'company')}
                style={{ width: '97.5%', margin: 0 }}
              />
            </div>
          </div>
          <div className='row' style={{ paddingTop: 10 }}>
            <div className='col-sm-12'>
              <label style={getLabelStyles('cardNumber', { margin: 0, fontSize: 12 })}>
                Card Number:
              </label>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12'>
              <input
                type='text'
                placeholder='Card Number'
                value={formatCardNumber(form.cardNumber)}
                onChange={(e) => handleFormChange(e, 'cardNumber')}
                style={getInputStyles('cardNumber', { width: '97.5%', margin: 0 })}
              />
            </div>
          </div>
          <div className='row' style={{ paddingTop: 10 }}>
            <div className='col-sm-3'>
              <label style={getLabelStyles('expMonth', { margin: 0, fontSize: 12 })}>
                Exp Month:
              </label>
            </div>
            <div className='col-sm-3'>
              <label style={getLabelStyles('expYear', { margin: 0, fontSize: 12 })}>
                Exp Year:
              </label>
            </div>
            <div className='col-sm-3'>
              <label style={getLabelStyles('cvv', { margin: 0, fontSize: 12 })}>
                CVV:
              </label>
            </div>
            <div className='col-sm-3' style={{ padding: 0 }}>
              <Tooltip title='Check this if billing address is outside US, Canada, or Puerto Rico' placement='top'>
                <label style={getLabelStyles('internationalAddress', { margin: 0, fontSize: 12 })}>
                  International Card <HelpOutlineIcon style={{ fontSize: 16 }} />:
                </label>
              </Tooltip>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-3'>
              <select
                placeholder='Exp. Month'
                value={form.expMonth}
                onChange={(e) => handleFormChange(e, 'expMonth')}
                style={getInputStyles('expMonth', { width: '100%', margin: 0, padding: '3px 8px', height: 'auto' })}
              >
                <option value=''></option>
                <option value='01'>1</option>
                <option value='02'>2</option>
                <option value='03'>3</option>
                <option value='04'>4</option>
                <option value='05'>5</option>
                <option value='06'>6</option>
                <option value='07'>7</option>
                <option value='08'>8</option>
                <option value='09'>9</option>
                <option value='10'>10</option>
                <option value='11'>11</option>
                <option value='12'>12</option>
              </select>
            </div>
            <div className='col-sm-3'>
              <select
                  placeholder='Exp. Year'
                  value={form.expYear}
                  onChange={(e) => handleFormChange(e, 'expYear')}
                  style={getInputStyles('expYear', { width: '100%', margin: 0, padding: '3px 8px', height: 'auto' })}
                >
                  <option value=''></option>
                  {expirationYears.map(year => {
                    return (
                      <option key={year} value={year}>{year}</option>
                    )
                  })}
              </select>
            </div>
            <div className='col-sm-3'>
              <input
                type='text'
                placeholder='CVV'
                value={form.cvv}
                onChange={(e) => handleFormChange(e, 'cvv')}
                style={getInputStyles('cvv', { width: '92.5%', margin: 0 })}
              />
            </div>
            <div className='col-sm-3'>
              <input
                type='checkbox'
                checked={form.internationalAddress}
                onChange={() => setForm({ ...form, internationalAddress: !form.internationalAddress })}
                style={getInputStyles('internationalAddress', { width: '92.5%', margin: 0 })}
              />
            </div>
          </div>
          {!form.internationalAddress ?
            <>
              <div className='row' style={{ paddingTop: 10 }}>
                <div className='col-sm-6'>
                  <label style={getLabelStyles('address1', { margin: 0, fontSize: 12 })}>
                    Address Line 1:
                  </label>
                </div>
                <div className='col-sm-4'>
                  <label style={{ margin: 0, fontSize: 12 }}>
                    Address Line 2:
                  </label>
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-6'>
                  <input
                    type='text'
                    placeholder='Address Line 1'
                    value={form.address1}
                    onChange={(e) => handleFormChange(e, 'address1')}
                    style={getInputStyles('address1', { width: '95%', margin: 0 })}
                  />
                </div>
                <div className='col-sm-6'>
                  <input
                    type='text'
                    placeholder='Address Line 2'
                    value={form.address2}
                    onChange={(e) => handleFormChange(e, 'address2')}
                    style={{ width: '95%', margin: 0 }}
                  />
                </div>
              </div>
              <div className='row' style={{ paddingTop: 10 }}>
                <div className='col-sm-4'>
                  <label style={getLabelStyles('postalCode', { margin: 0, fontSize: 12 })}>
                    Postal Code:
                  </label>
                </div>
                <div className='col-sm-4'>
                  <label style={getLabelStyles('city', { margin: 0, fontSize: 12 })}>
                    City:
                  </label>
                </div>
                <div className='col-sm-4'>
                  <label style={getLabelStyles('state', { margin: 0, fontSize: 12 })}>
                    State:
                  </label>
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-4'>
                  <input
                    type='text'
                    placeholder='Postal Code'
                    value={form.postalCode}
                    onChange={(e) => handleFormChange(e, 'postalCode')}
                    style={getInputStyles('postalCode', { width: '92.5%', margin: 0 })}
                  />
                </div>
                <div className='col-sm-4'>
                  <input
                    type='text'
                    placeholder='City'
                    value={form.city}
                    onChange={(e) => handleFormChange(e, 'city')}
                    style={getInputStyles('city', { width: '92.5%', margin: 0 })}
                  />
                </div>
                <div className='col-sm-4'>
                  <select
                    placeholder='State'
                    value={form.state}
                    onChange={(e) => handleFormChange(e, 'state')}
                    style={getInputStyles('state', { width: '100%', margin: 0, padding: '3px 8px', height: 'auto' })}
                  >
                    <option value=''></option>
                    {props.states.map(state => {
                      return (
                        <option key={state.state_id} value={state.state_id}>{state.state_name}</option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </>
          :
            <>
              <div className='row' style={{ paddingTop: 10 }}>
                <div className='col-sm-12'>
                  <label style={getLabelStyles('addressBlock', { margin: 0, fontSize: 12 })}>
                    Address Block:
                  </label>
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-12'>
                  <textarea
                    placeholder='Address Block'
                    value={form.addressBlock}
                    onChange={(e) => handleFormChange(e, 'addressBlock')}
                    style={getInputStyles('addressBlock', { width: '100%', margin: 0, height: 70 })}
                  />
                </div>
              </div>
            </>
          }
          <div className='row'>
            <div className='col-sm-12'>
              <Render if={props.updateCreditCardMeta.failure}>
                <p style={{ color: 'red', margin: 0, fontSize: 14 }}>{props.updateCreditCardMeta.message}</p>
              </Render>
              <Render if={props.updateCreditCardMeta.success}>
                <p style={{ color: 'green', margin: 0, fontSize: 14 }}>Credit Card updated successfully</p>
              </Render>
            </div>
          </div>
          <div className='row' style={{ textAlign: 'right', paddingTop: 10 }}>
            <div className='col-sm-12'>
              <button
                className={buttonStyles.cancelButton}
                onClick={(e) => handleCancel(e)}
                style={{ marginLeft: 5 }}
              >
                Cancel
              </button>
              <button
                className={buttonStyles.submitButton}
                onClick={(e) => handleSave(e)}
                style={{ marginLeft: 5 }}
              >
                Save and Authorize
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}